/**
 * Date formatting utilities for consistent MM/DD/YYYY display format
 */

/**
 * Converts various date formats to MM/DD/YYYY
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string in MM/DD/YYYY format, or empty string if invalid
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "";
  
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) return "";
    
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    const year = dateObj.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return "";
  }
}

/**
 * Converts MM/DD/YYYY to YYYY-MM-DD for HTML date inputs
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Date string in YYYY-MM-DD format, or empty string if invalid
 */
export function toInputDateFormat(dateStr: string): string {
  if (!dateStr) return "";
  
  try {
    const parts = dateStr.split("/");
    if (parts.length !== 3) return "";
    
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  } catch (error) {
    return "";
  }
}

/**
 * Gets today's date in YYYY-MM-DD format for HTML date inputs
 * @returns Today's date in YYYY-MM-DD format
 */
export function getTodayInputFormat(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Gets today's date in MM/DD/YYYY format
 * @returns Today's date in MM/DD/YYYY format
 */
export function getTodayFormatted(): string {
  return formatDate(new Date());
}

/**
 * Formats a phone number string to (XXX) XXX-XXXX as the user types.
 * Strips non-digits and applies the mask progressively.
 * @param value - Raw or partially-formatted phone string
 * @returns Formatted phone string
 */

/**
 * Formats a phone number string to ###-###-#### as the user types.
 * @param value - Raw or partially-formatted phone string
 * @returns Formatted phone string
 */
export function formatPhoneNumberDash(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function formatDateOfBirth(value: string): string {
  if (!value) return "";

  const daysInMonth: Record<number, number> = {
    1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
    7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
  };

  const clampMonth = (mm: string) => {
    if (mm.length < 2) return mm;
    const m = parseInt(mm, 10);
    if (m < 1) return "01";
    if (m > 12) return "12";
    return mm;
  };

  const clampDay = (dd: string, mm: string) => {
    if (dd.length < 2) return dd;
    const monthNum = parseInt(mm, 10) || 1;
    const maxDay = daysInMonth[monthNum] ?? 31;
    const d = parseInt(dd, 10);
    if (d < 1) return "01";
    if (d > maxDay) return String(maxDay).padStart(2, "0");
    return dd;
  };

  // When value already has slashes, parse each segment independently
  // so editing one segment doesn't corrupt the others.
  if (value.includes("/")) {
    const parts = value.split("/");
    const rawMM = parts[0].replace(/\D/g, "").slice(0, 2);
    const rawDD = (parts[1] ?? "").replace(/\D/g, "").slice(0, 2);
    const rawYYYY = (parts[2] ?? "").replace(/\D/g, "").slice(0, 4);

    // Segment lengths are within bounds — structure is intact, edit in place
    if (parts[0].length <= 2 && (parts[1] ?? "").length <= 2 && (parts[2] ?? "").length <= 4) {
      const mm = clampMonth(rawMM);
      const dd = clampDay(rawDD, mm);
      if (parts.length === 1) return mm;
      if (parts.length === 2) return `${mm}/${dd}`;
      return `${mm}/${dd}/${rawYYYY}`;
    }
    // Slashes are in wrong positions (e.g. "/" was deleted and digits merged)
    // — fall through to digit-strip reformat below
  }

  // Fresh entry (no slashes yet): strip non-digits and auto-insert slashes
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (!digits) return "";

  const mm = clampMonth(digits.slice(0, 2));
  if (digits.length <= 2) return mm;

  const dd = clampDay(digits.slice(2, 4), mm);
  if (digits.length <= 4) return `${mm}/${dd}`;

  return `${mm}/${dd}/${digits.slice(4)}`;
}
